import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button, Table, Row, Col, Card, CardBody,
    Badge, Pagination, PaginationItem, PaginationLink, Label, Input
} from "reactstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import { FiSearch, FiRefreshCcw } from "react-icons/fi";

// MUI Date Picker Imports
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TextField } from '@mui/material';
import dayjs from 'dayjs';

const StaffDepositHistory = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [history, setHistory] = useState([]);
    const [staffOptions, setStaffOptions] = useState([]);
    const [staffInfo, setStaffInfo] = useState(null);
    const [summary, setSummary] = useState({ total_amount: 0 });
    const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1 });

    const [filters, setFilters] = useState({
        staff_id: "",
        from_date: null,
        to_date: null,
        page: 1,
        limit: 10,
        sort_order: "DESC"
    });

    useEffect(() => {
        const getStaffs = async () => {
            // Start-laye loading true panrathu nallathu machan
            dispatch(loadingTrue());
            try {
                const res = await Handler({ method: "GET", url: "/staff/list.php" });

                if (res?.success) {
                    // 🔥 Machan, staff.staff.name structure-ku mathiyachu
                    const formattedOptions = res.data.map(staff => ({
                        value: staff.id,
                        // staff object kulla staff name irundha edukum, illana fallback check
                        label: staff.staff?.name?.toUpperCase() || staff.name?.toUpperCase() || "UNKNOWN STAFF"
                    }));

                    setStaffOptions(formattedOptions);
                } else {
                    console.error("Staff fetch failed:", res?.message);
                }
            } catch (err) {
                toast.error("Error loading staff list");
                console.error("Staff API error:", err);
            } finally {
                // Error vanthaalum, success aanaalum loading stop aaganum
                dispatch(loadingFalse());
            }
        };
        getStaffs();
    }, [dispatch]); // dispatch-ah dependency-la sethukko machan, best practice

    const loadHistory = async () => {
        if (!filters.staff_id) return;

        dispatch(loadingTrue());
        try {
            const params = {
                ...filters,
                from_date: filters.from_date ? dayjs(filters.from_date).format('YYYY-MM-DD') : "",
                to_date: filters.to_date ? dayjs(filters.to_date).format('YYYY-MM-DD') : ""
            };
            const queryParams = new URLSearchParams(params).toString();
            const res = await Handler({ method: "GET", url: `/deposit/history.php?${queryParams}` });

            if (res?.success) {
                setHistory(res.data.history || []);
                setStaffInfo(res.data.staff);
                setSummary(res.data.summary);
                setPagination({
                    current_page: res.page,
                    total_pages: res.total_pages,
                    total_records: res.total
                });
                dispatch(loadingFalse());
            } else {
                toast.error(res?.message || "Failed to load history");
                dispatch(loadingFalse());
            }
        } catch (err) {
            toast.error("Network error occurred");
            dispatch(loadingFalse());
        } finally {
            dispatch(loadingFalse());
        }
    };

    useEffect(() => {
        loadHistory();
    }, [filters.page, filters.staff_id, filters.sort_order]);

    // 🔥 Uniform Height Styles
    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: '50px',
            height: '50px',
            borderRadius: '10px',
            borderColor: '#dee2e6',
            boxShadow: 'none',
            '&:hover': { borderColor: '#f16436' }
        }),
        valueContainer: (provided) => ({ ...provided, height: '50px', padding: '0 12px' }),
        indicatorsContainer: (provided) => ({ ...provided, height: '50px' }),
    };

    const muiPickerStyles = {
        '& .MuiInputBase-root': {
            height: '50px',
            borderRadius: '10px',
            backgroundColor: '#f8f9fa',
            border: 'none',
            '& fieldset': { border: 'none' },
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-3 bg-light min-vh-100">
                <div className="d-md-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h3 className="fw-bold m-0 text-dark">Staff Deposit Ledger</h3>
                        <p className="text-muted small m-0">Detailed allocation tracking.</p>
                    </div>
                    <div className="d-flex gap-2">
                        <Button color="white" className="shadow-sm border rounded-pill px-3" onClick={loadHistory}><FiRefreshCcw /></Button>
                        <Button color="primary" className="add-btn px-4 shadow-sm" onClick={() => navigate("/staff/deposit/add")}>+ New Deposit</Button>
                    </div>
                </div>

                <Card className="border-0 shadow-sm rounded-4 mb-4">
                    <CardBody className="p-3">
                        <Row className="g-3 align-items-end">
                            <Col lg={3} md={6}>
                                <Label className="small fw-bold text-muted text-uppercase">Staff Member</Label>
                                <Select
                                    options={staffOptions}
                                    placeholder="Choose..."
                                    isClearable
                                    styles={customSelectStyles}
                                    onChange={(opt) => setFilters({ ...filters, staff_id: opt?.value || "", page: 1 })}
                                />
                            </Col>
                            <Col lg={2} md={6}>
                                <Label className="small fw-bold text-muted text-uppercase">From Date</Label>
                                <DatePicker
                                    disableFuture
                                    format="DD/MM/YYYY"
                                    value={filters.from_date}
                                    onChange={(date) => setFilters({ ...filters, from_date: date, page: 1 })}
                                    slotProps={{ textField: { fullWidth: true, sx: muiPickerStyles } }}
                                />
                            </Col>
                            <Col lg={2} md={6}>
                                <Label className="small fw-bold text-muted text-uppercase">To Date</Label>
                                <DatePicker
                                    disableFuture
                                    format="DD/MM/YYYY"
                                    value={filters.to_date}
                                    minDate={filters.from_date ? filters.from_date : undefined}
                                    onChange={(date) => setFilters({ ...filters, to_date: date, page: 1 })}
                                    slotProps={{ textField: { fullWidth: true, sx: muiPickerStyles } }}
                                />
                            </Col>
                            <Col lg={2} md={6}>
                                <Label className="small fw-bold text-muted text-uppercase">Order</Label>
                                <Input
                                    type="select"
                                    style={{ height: '50px', borderRadius: '10px' }}
                                    className="bg-light border-0"
                                    value={filters.sort_order}
                                    onChange={(e) => setFilters({ ...filters, sort_order: e.target.value })}
                                >
                                    <option value="DESC">Newest</option>
                                    <option value="ASC">Oldest</option>
                                </Input>
                            </Col>
                            <Col lg={3} md={12}>
                                <Button color="primary" block className="fw-bold shadow-sm rounded-3" style={{ height: '50px' }} onClick={loadHistory}>
                                    <FiSearch className="me-1" /> Search Records
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {staffInfo && (
                    <div className="bg-white p-3 rounded-4 shadow-sm border-start border-4 border-primary d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <small className="text-muted text-uppercase fw-bold">Active Staff</small>
                            <h5 className="m-0 fw-bold">{staffInfo.name}</h5>
                        </div>
                        <div className="text-end">
                            <small className="text-muted text-uppercase fw-bold">Total Ledger</small>
                            <h4 className="m-0 text-primary fw-bold">₹ {summary.total_amount?.toLocaleString()}</h4>
                        </div>
                    </div>
                )}

                <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                    <CardBody className="p-0">
                        <Table responsive hover className="mb-0">
                            <thead className="bg-light">
                                <tr className="text-muted x-small uppercase ls-1">
                                    <th className="ps-4 py-3">S.No</th>
                                    <th>Date</th>
                                    <th>Amount (₹)</th>
                                    <th>Note</th>
                                    <th className="pe-4">Issued By</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.length > 0 ? history.map((item, index) => (
                                    <tr key={item.id}>
                                        <td className="ps-4">{(filters.page - 1) * filters.limit + index + 1}</td>
                                        <td>{dayjs(item.created_at).format('DD MMM YYYY')}</td>
                                        <td className="fw-bold text-success">₹ {parseFloat(item.amount)?.toLocaleString()}</td>
                                        <td className="small">{item.note || "--"}</td>
                                        <td className="pe-4"><Badge color="soft-primary" className="text-primary">{item.created_by_name}</Badge></td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="5" className="text-center py-5 text-muted">No records found.</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>

                {pagination?.total_pages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                        <Pagination size="sm">
                            <PaginationItem disabled={filters.page <= 1}>
                                <PaginationLink previous onClick={() => setFilters({ ...filters, page: filters.page - 1 })} />
                            </PaginationItem>
                            {[...Array(pagination?.total_pages)].map((_, i) => (
                                <PaginationItem active={i + 1 === filters.page} key={i}>
                                    <PaginationLink onClick={() => setFilters({ ...filters, page: i + 1 })}>{i + 1}</PaginationLink>
                                </PaginationItem>
                            ))}
                            <PaginationItem disabled={filters.page >= pagination?.total_pages}>
                                <PaginationLink next onClick={() => setFilters({ ...filters, page: filters.page + 1 })} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                )}
            </div>
        </LocalizationProvider>
    );
};

export default StaffDepositHistory;